import { SurveyType } from '@commutifi/models/Surveys'
import { HttpStatusCode } from '@commutifi-fe/constants'
import { getAuthenticatedAccountId } from 'api/index'
import { FormattedMessage } from 'locales/index'
import get from 'lodash/get'
import React from 'react'
import { connect, ConnectedComponent } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { Button as AppButton, ErrorBoundary, GenericError, PageLoader } from '@commutifi-fe/ui'
import { ExtendedAccount, RequestParams } from 'shared/interfaces'
import { useConfig } from 'shared/providers'
import { PublicPathsParams, PublicRouting } from 'src/routing'
import { authActions, authSelectors } from 'store/modules/auth'
import { selectors as surveySelectors } from 'store/modules/enterpriseSurvey'
import { loadDefaultSurvey, loadEnterpriseSurvey } from 'store/modules/enterpriseSurvey/sagas'
import { RootState } from 'store/index'
import { wrapActionInPromise } from 'utils/helpers'
import { identify } from 'utils/logRocket'
import { parse } from 'utils/queryParams'

interface OwnProps extends RouteComponentProps<{ [PublicPathsParams.Shortlink]: string }> {
  accountContext?: ExtendedAccount
  children: React.ReactNode
}

interface ReduxProps {
  loaded: boolean
  loading: boolean
  loadEnterpriseSurveyError: any
  loadEnterpriseSurvey: (
    params: RequestParams & { shortlinkId?: string },
    config: { forceReset?: boolean; initialData: any }
  ) => void
  loadDefaultSurvey: (params: RequestParams & { accountId?: string }, config: { initialData: any }) => void
  isAuthenticated: boolean
  logout: typeof authActions.logout
  prefilledEnterpriseId: string
  prefilledOrganizationId: string
  prefilledAccountId: string
  prefilledBuildingId: string
  shortlinkId: string
  surveyType: SurveyType
}

export type OnboardingProps = OwnProps & ReduxProps

export function Onboarding({
  loadEnterpriseSurvey,
  loadDefaultSurvey,
  loadEnterpriseSurveyError,
  match,
  location,
  history,
  loaded,
  loading,
  isAuthenticated,
  logout,
  shortlinkId,
  prefilledEnterpriseId,
  prefilledOrganizationId,
  prefilledAccountId,
  prefilledBuildingId,
  surveyType,
  accountContext,
  children
}: OnboardingProps) {
  const { locale } = useConfig()
  const handlePageReload = () => {
    window.location.reload()
  }
  const handleRedirectHome = () => {
    history.push(PublicRouting.root.path)
  }
  const handleLogout = async () => {
    await wrapActionInPromise(logout, {})
    handlePageReload()
  }

  const storedAccountId = React.useMemo(() => getAuthenticatedAccountId(), [])

  React.useEffect(() => {
    identify({
      prefilledEnterpriseId,
      prefilledOrganizationId,
      prefilledAccountId,
      prefilledBuildingId,
      shortlinkId
    })
  }, [shortlinkId, prefilledEnterpriseId, prefilledOrganizationId, prefilledAccountId, prefilledBuildingId])

  const urlShortlink = match.params.shortlink

  React.useEffect(() => {
    // Make sure to avoid refetching survey when we are redirected back from SSO flow
    if (get(parse(location.search), 'code')) return

    // Refetch the survey when the shortlink changes in the url or if the user switch locale
    if (match.isExact || !loaded || (loaded && urlShortlink !== shortlinkId)) {
      if (storedAccountId && urlShortlink === '_') {
        loadDefaultSurvey(
          // Sending account id to make sure we can get the user default survey. If we
          // have a shortlink id we don't need to fetch the default survey -> we don't need the account id
          { accountId: storedAccountId, relations: ['survey', 'enterprise'] },
          { initialData: { accountContext } }
        )
      } else {
        loadEnterpriseSurvey(
          {
            shortlinkId: urlShortlink,
            relations: ['survey', 'enterprise']
          },
          { forceReset: match.isExact, initialData: { accountContext } }
        )
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Keep minimal updates
  }, [urlShortlink, locale])

  if (loadEnterpriseSurveyError || (loaded && !surveyType)) {
    return (
      <GenericError
        error={loadEnterpriseSurveyError || { status: HttpStatusCode.NotFound }}
        type="page"
        title={isAuthenticated ? <FormattedMessage id="onboarding.forbidden.error.title" /> : undefined}
        actions={
          isAuthenticated ? (
            <div className="u-flex-column">
              <AppButton type="primary" onClick={handleLogout} className="u-margin-vertical--medium-2">
                <FormattedMessage id="logout" />
              </AppButton>
              <AppButton ghost onClick={handleRedirectHome}>
                <FormattedMessage id="onboarding.forbidden.button.accessDashboard" />
              </AppButton>
            </div>
          ) : (
            <AppButton type="primary" onClick={handlePageReload}>
              <FormattedMessage id="global.page.reload" />
            </AppButton>
          )
        }
      />
    )
  }

  if (!loaded || loading) {
    return <PageLoader />
  }

  return <ErrorBoundary ErrorComponent={GenericError}>{children}</ErrorBoundary>
}

function mapStateToProps<T extends Record<string, any>>(state: RootState, ownProps: T) {
  return {
    loaded: surveySelectors.getIsLoaded(state),
    loading: surveySelectors.getIsLoading(state),
    loadEnterpriseSurveyError: surveySelectors.getError(state),
    backgroundImage: surveySelectors.makeGetSurvey('backgroundImage')(state),
    surveyType: surveySelectors.makeGetSurvey('type')(state),
    introHeader: surveySelectors.makeGetContent('introHeader')(state),
    introContent: surveySelectors.makeGetContent('introContent')(state),
    showOverview: surveySelectors.makeGetSurvey('showOverview')(state),
    prefilledEnterpriseId: surveySelectors.makeGetPrefilledData('enterprise.id')(state),
    prefilledOrganizationId: surveySelectors.makeGetPrefilledData('organization.id')(state),
    prefilledAccountId: surveySelectors.makeGetPrefilledData('account.id')(state),
    prefilledBuildingId: surveySelectors.makeGetPrefilledData('building.id')(state),
    shortlinkId: surveySelectors.makeGetPrefilledData('id')(state),
    introName:
      surveySelectors.makeGetPrefilledData('enterprise.name')(state) ||
      surveySelectors.makeGetPrefilledData('organization.name')(state),
    username: surveySelectors.makeGetPrefilledData('account.name')(state),
    logo: surveySelectors.getSurveyLogo(state),
    address: surveySelectors.makeGetPrefilledData('building.address')(state),
    isAuthenticated: authSelectors.isLoggedIn(state),
    ...ownProps
  }
}

const mapDispatchToProps = {
  loadEnterpriseSurvey,
  loadDefaultSurvey,
  logout: authActions.logout
}

export function withSurveyContext<T extends Record<string, any>>(Component: React.FC<ReduxProps & OwnProps & T>) {
  return connect(
    mapStateToProps,
    mapDispatchToProps
  )((props: ReduxProps & OwnProps & T) => <Component {...props} />) as ConnectedComponent<
    React.JSXElementConstructor<T>,
    T
  >
}
